import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getSubTenants, getTenantById } from '../../../services/api';
import './Tenants.css';

// Interface para o Tenant
interface Tenant {
  id: string;
  name: string;
  email: string;
  credits: number;
  active: boolean;
  createdAt: string;
  updatedAt?: string;
  phone?: string;
  document?: string;
  address?: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  active: boolean;
  isTenantAdmin: boolean;
}

interface SubTenant extends Tenant {
  users: User[];
}

interface SubTenantsResponse {
  success: boolean;
  subTenants: SubTenant[];
  error?: string;
}

const TenantSubtenants: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [subTenants, setSubTenants] = useState<SubTenant[]>([]);
  const [parentTenant, setParentTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Carregar os dados do tenant pai e seus sub-tenants
  useEffect(() => {
    if (!id) return;
    
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Buscar informações do tenant pai
        const parentResponse = await getTenantById(id);
        
        if (parentResponse.success && parentResponse.tenant) {
          setParentTenant(parentResponse.tenant);
        } else {
          setError(parentResponse.message || 'Erro ao carregar dados do cliente pai');
          setLoading(false);
          return;
        }
        
        // Buscar os sub-tenants
        const response = await getSubTenants(id);
        
        if (response.success) {
          setSubTenants(response.subTenants);
        } else {
          setError(response.error || 'Erro ao carregar subclientes');
        }
      } catch (error) {
        setError('Erro ao conectar com o servidor');
        console.error('Erro ao buscar subclientes:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  const handleAddSubTenant = () => {
    navigate('/admin/tenants/add-subtenant', { state: { parentId: id } });
  };

  return (
    <div className="tenants-container">
      <div className="tenants-header">
        <div className="breadcrumb">
          <Link to="/admin/tenants">Clientes</Link>
          {parentTenant && (
            <>
              <span className="separator">&gt;</span>
              <Link to={`/admin/tenants/${id}`}>{parentTenant.name}</Link>
              <span className="separator">&gt;</span>
              <span>Subclientes</span>
            </>
          )}
        </div>
        
        <h2>
          {parentTenant ? `Subclientes de ${parentTenant.name}` : 'Subclientes'}
        </h2>

        <div className="actions">
          <button onClick={handleAddSubTenant} className="btn-add-tenant">
            Adicionar Subcliente
          </button>
          <Link to={`/admin/tenants/${id}`} className="btn-back">
            Voltar ao Cliente
          </Link>
        </div>
      </div>

      {loading && <div className="loading">Carregando subclientes...</div>}
      
      {error && <div className="error-message">{error}</div>}

      {!loading && subTenants.length === 0 && (
        <div className="no-tenants">
          Nenhum subcliente cadastrado. Clique em "Adicionar Subcliente" para criar o primeiro.
        </div>
      )}

      {!loading && subTenants.length > 0 && (
        <div className="tenants-table-container">
          <table className="tenants-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Email</th>
                <th>Telefone</th>
                <th>Usuários</th>
                <th>Créditos</th>
                <th>Status</th>
                <th>Data Cadastro</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {subTenants.map(tenant => (
                <tr key={tenant.id} className={!tenant.active ? 'inactive-tenant' : ''}>
                  <td>{tenant.name}</td>
                  <td>{tenant.email}</td>
                  <td>{tenant.phone || '-'}</td>
                  <td>{tenant.users.length}</td>
                  <td>{tenant.credits.toFixed(2)}</td>
                  <td>
                    <span className={`status-badge ${tenant.active ? 'active' : 'inactive'}`}>
                      {tenant.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td>{formatDate(tenant.createdAt)}</td>
                  <td className="actions-cell">
                    <Link to={`/admin/tenants/${tenant.id}`} className="btn-view">
                      Ver Detalhes
                    </Link>
                    <Link to={`/admin/tenants/${tenant.id}/subtenants`} className="btn-subtenants">
                      Subclientes
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TenantSubtenants; 